import { useEffect, useState } from "react";

import { ApiError } from "@/shared/api";
import { moduleBlockReasonOf, type ModuleBlockReason } from "@/shared/lib/forbiddenReason";

import { acqService } from "../api/acqService";

/**
 * 获客舱的「公司没开通」门(修复波 0926 第十一批 B2 #20)。
 *
 * ★K2 实测(基础版 QA 租户 T-9D94E08C):直访 /ai-huoke 渲染舱壳 + 后端原句「当前租户未开通该模块 重试」,
 *   /ai-huoke/tools 三张卡完整可见(那页根本不调接口)。ProtectedRoute 的 module 门在 permissions 缓存
 *   陈旧时兜不住,舱自己要认接口的 403 MODULE_NOT_ENABLED。
 * ★探法:问一次目录口(`acqService.catalog`,舱的入口接口)。403 MODULE_NOT_ENABLED → blocked;
 *   其它错误(网络 / 500)不算没开通,照常渲染让页面自己报错。结果按会话缓存 60s,同舱内切 tab 不重问。
 * ★渲染期先放行、探出没开通再跳:不为一次探测把整舱变成白屏。
 */
export function isModuleNotEnabledError(e: unknown): boolean {
  return e instanceof ApiError && e.errorCode === "MODULE_NOT_ENABLED";
}

const TTL_MS = 60_000;
/** OEM C4:缓存的是「被拦原因」(PAUSED / NOT_OWNED),null = 放行;落 403 页时按原因分话术。 */
let cached: { blocked: ModuleBlockReason | null; at: number } | null = null;
let inflight: Promise<ModuleBlockReason | null> | null = null;

/** 测试用:清会话缓存。 */
export function __resetAcqModuleGate() {
  cached = null;
  inflight = null;
}

function probe(): Promise<ModuleBlockReason | null> {
  if (cached && Date.now() - cached.at < TTL_MS) return Promise.resolve(cached.blocked);
  if (!inflight) {
    inflight = acqService
      .catalog()
      .then(
        () => null,
        (e) => moduleBlockReasonOf(e)
      )
      .then((blocked) => {
        cached = { blocked, at: Date.now() };
        inflight = null;
        return blocked;
      });
  }
  return inflight;
}

/** 返回被拦原因(truthy = 没开通 / 被暂停);null = 放行。 */
export function useAcqModuleGate(): ModuleBlockReason | null {
  const [blocked, setBlocked] = useState<ModuleBlockReason | null>(() => cached?.blocked ?? null);
  useEffect(() => {
    let alive = true;
    void probe().then((b) => {
      if (alive) setBlocked(b);
    });
    return () => {
      alive = false;
    };
  }, []);
  return blocked;
}
